import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2 } from "lucide-react";

export default function ProductForm({ product, categories, onClose, onSaved }) {
  const [f, setF] = useState({
    name: product?.name || "",
    description: product?.description || "",
    category_id: product?.category_id || categories[0]?.id || "",
    price: product?.price || 0,
    promo_price: product?.promo_price || "",
    cost_price: product?.cost_price || 0,
    stock: product?.stock || 0,
    images: product?.images || [],
    tags: product?.tags?.join(", ") || "",
    age_range: product?.age_range || "",
    material: product?.material || "",
    weight_g: product?.weight_g || 100,
    featured: !!product?.featured,
    bestseller: !!product?.bestseller,
    new_arrival: !!product?.new_arrival,
    active: product?.active !== false,
    sku: product?.sku || "",
  });
  const [imgInput, setImgInput] = useState("");
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const addImg = () => {
    if (!imgInput) return;
    upd("images", [...f.images, imgInput]);
    setImgInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        price: Number(f.price),
        promo_price: f.promo_price ? Number(f.promo_price) : null,
        cost_price: Number(f.cost_price) || 0,
        stock: Number(f.stock) || 0,
        weight_g: Number(f.weight_g) || 100,
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        variants: [],
      };
      if (product) {
        await api.put(`/admin/products/${product.id}`, payload);
        toast.success("Produto atualizado!");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produto criado!");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#4A3B40]">{product ? "Editar produto" : "Novo produto"}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <input className="pf" placeholder="Nome do produto" value={f.name} onChange={(e)=>upd("name", e.target.value)} data-testid="pf-name" />
          <select className="pf" value={f.category_id} onChange={(e)=>upd("category_id", e.target.value)} data-testid="pf-category">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input className="pf" placeholder="SKU (auto)" value={f.sku} onChange={(e)=>upd("sku", e.target.value)} />
          <input className="pf" type="number" step="0.01" placeholder="Preço (R$)" value={f.price} onChange={(e)=>upd("price", e.target.value)} data-testid="pf-price" />
          <input className="pf" type="number" step="0.01" placeholder="Preço promo (opcional)" value={f.promo_price} onChange={(e)=>upd("promo_price", e.target.value)} />
          <input className="pf" type="number" step="0.01" placeholder="Custo (R$)" value={f.cost_price} onChange={(e)=>upd("cost_price", e.target.value)} />
          <input className="pf" type="number" placeholder="Estoque" value={f.stock} onChange={(e)=>upd("stock", e.target.value)} data-testid="pf-stock" />
          <input className="pf" type="number" placeholder="Peso (g)" value={f.weight_g} onChange={(e)=>upd("weight_g", e.target.value)} />
          <input className="pf" placeholder="Faixa etária" value={f.age_range} onChange={(e)=>upd("age_range", e.target.value)} />
          <input className="pf" placeholder="Material" value={f.material} onChange={(e)=>upd("material", e.target.value)} />
          <textarea className="pf md:col-span-2" rows={3} placeholder="Descrição" value={f.description} onChange={(e)=>upd("description", e.target.value)} data-testid="pf-description" />
          <input className="pf md:col-span-2" placeholder="Tags (separadas por vírgula)" value={f.tags} onChange={(e)=>upd("tags", e.target.value)} />
        </div>

        <div className="mt-4">
          <p className="font-semibold text-[#4A3B40] mb-2">Imagens</p>
          <div className="flex gap-2 mb-2">
            <input className="pf flex-1" placeholder="URL da imagem" value={imgInput} onChange={(e)=>setImgInput(e.target.value)} />
            <button onClick={addImg} className="btn-lara-soft py-2 px-4"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {f.images.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button onClick={()=>upd("images", f.images.filter((_,j)=>j!==i))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1"><Trash2 className="w-3 h-3 text-[#E57373]" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e)=>upd("featured", e.target.checked)} /> Destaque</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.bestseller} onChange={(e)=>upd("bestseller", e.target.checked)} /> Mais vendido</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.new_arrival} onChange={(e)=>upd("new_arrival", e.target.checked)} /> Novidade</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.active} onChange={(e)=>upd("active", e.target.checked)} /> Ativo</label>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="btn-lara-outline">Cancelar</button>
          <button onClick={save} disabled={saving} className="btn-lara" data-testid="pf-save">{saving?"Salvando...":"Salvar produto"}</button>
        </div>
        <style>{`.pf{width:100%;border:1px solid #F9E1E8;background:#FFF9FA;border-radius:12px;padding:10px 14px;outline:none;font-size:14px;color:#4A3B40}.pf:focus{border-color:#C9708B}`}</style>
      </div>
    </div>
  );
}
